import React from 'react';
import { FaUserClock } from 'react-icons/fa';
import { SectionCard, MiniStat } from './ui';

const query = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

interface Props {
  /** `approvals` block of the role dashboard payload. */
  approvals: Record<string, number | undefined> | null | undefined;
}

/**
 * The Approvals section shared by the role dashboards.
 *
 * Every tile is a link to the list page behind its number, opened in a new tab
 * with the filters that produce it — the list pages read them on mount through
 * `useUrlFilters`. A tile is shown only when the role's payload carries its
 * figure, so each role keeps the set of tiles it always had.
 */
export const ApprovalsSection: React.FC<Props> = ({ approvals }) => {
  if (!approvals) return null;
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const todayRange = { attendance_date_from: today, attendance_date_to: today };

  const tiles: Array<{ key: string; label: string; to: string; color: 'blue' | 'green' | 'yellow' | 'red' | 'gray' }> = [
    { key: 'attendance_marked_direct_team', label: 'Attendance — Direct', to: `/attendance/team${query(todayRange)}`, color: 'blue' },
    { key: 'attendance_marked_all_team', label: 'Attendance — All', to: `/attendance/project-team${query(todayRange)}`, color: 'green' },
    { key: 'attendance_pending_direct_team', label: 'Attendance — Pending Approval (Direct)', to: `/attendance/team${query({ hr_status: 'pending' })}`, color: 'red' },
    { key: 'attendance_pending_all_team', label: 'Attendance — Pending Approval (All)', to: `/attendance/project-team${query({ hr_status: 'pending' })}`, color: 'red' },
    { key: 'leaves_applied', label: 'Leaves Applied', to: `/leaves${query({ leave_status: 'pending' })}`, color: 'yellow' },
    { key: 'advance_requested', label: 'Advances Requested', to: `/claims/advance${query({ status: 'requested' })}`, color: 'red' },
    { key: 'cab_hiring', label: 'Cab Hiring', to: `/claims/cab-hiring${query({ status: 'claim_submitted' })}`, color: 'blue' },
    { key: 'general_claims', label: 'General Claims', to: `/claims/general${query({ status_list: 'pending,submitted' })}`, color: 'green' },
    { key: 'ticket_open', label: 'Tickets Open', to: `/tickets/actions${query({ status: 'open' })}`, color: 'yellow' },
    { key: 'training_request_open', label: 'Training Open', to: '/training/requests', color: 'gray' },
    // Requested Sites opens on its Pending tab.
    { key: 'site_assign_requests_pending', label: 'Assign Requests Pending (Sites)', to: '/sites/requested', color: 'blue' },
  ];
  const shown = tiles.filter((tile) => approvals[tile.key] !== undefined && approvals[tile.key] !== null);

  return (
    <SectionCard title="Approvals" icon={FaUserClock}>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {shown.map((tile) => (
          <MiniStat key={tile.key} to={tile.to} newTab label={tile.label} value={approvals[tile.key] ?? 0} color={tile.color} />
        ))}
      </div>
    </SectionCard>
  );
};

export default ApprovalsSection;
